import * as React from 'react';
import { useState, useEffect } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import fetchAPI from 'app/helpers/fetch';
import { translateHtmlEntities } from 'app/helpers/strings';
import InfiniteScroll from 'app/helpers/InfiniteScroll';
import NameFilter from 'app/CasinoFilters/NameFilter';
import Badges from 'app/CasinoGames/Badges';
import useFilters from './useFilters';
import Filters from './Filters';

const BLOCK_CHECK_PERIOD = 5000;

const updateAccessBlock = (setAccessBlock) =>
  fetchAPI('/playtech/gallery/check_access.json')
    .then(({ block }) => block)
    .catch((err) => err)
    .then((result) => {
      setAccessBlock(result);
      return !!result;
    });

const useBlockCheck = (setAccessBlock) => {
  const timerHandler = async () => {
    await updateAccessBlock(setAccessBlock);
    setTimeout(timerHandler, BLOCK_CHECK_PERIOD);
  };

  useEffect(() => {
    setTimeout(timerHandler, BLOCK_CHECK_PERIOD);
  }, []);
};

const launchURL = (game, client) => {
  const { externalGameName, type } = game;
  const liveDealerNative = type === 'live_dealer' && client === 'native' ? 'poker_native_wrapped_live_url' : '';
  const liveDealer = type === 'live_dealer' ? 'live-game' : '';
  const gpas = externalGameName.startsWith('gpas_') ? 'gpas-game' : '';
  const urlid = liveDealerNative || liveDealer || gpas || 'ngm-game';
  return `htcmd:switchtogame?urlid=${urlid}&name=${encodeURIComponent(externalGameName)}`;
};

const useGallery = (block, client) => {
  const [loading, setLoading] = useState(false);
  const [accessBlock, setAccessBlock] = useState(block);
  useBlockCheck(setAccessBlock);

  const launch = async (game) => {
    if (loading) {
      return;
    }

    setLoading(true);
    const blockExists = await updateAccessBlock(setAccessBlock);
    setLoading(false);

    if (!blockExists) {
      window.location = launchURL(game, client);
    }
  };

  return [loading, accessBlock, launch];
};

const gameNameSize = (size) => {
  const names = [
    [40, 'marquee_longest'],
    [32, 'marquee_longer'],
    [27, 'marquee_long'],
    [21, 'marquee'],
  ];

  const name = names.find(([len]) => size >= len);
  return name ? name[1] : '';
};

const Game = ({ game, common, launch }) => {
  const name = translateHtmlEntities(game.gameLocaleName);
  const nameSize = gameNameSize(game.gameLocaleName.length);
  const gameLabelId = `game-label-${game.id}`;

  return (
    <li>
      <div className="game">
        <a
          aria-labelledby={gameLabelId}
          className="link"
          onClick={() => {
            launch(game);
          }}
        >
          <div className="img-wrap img-wrap_fixed">
            <img className="rs-game-placeholder" src={common.blankImageUrl} alt="" />
            <img
              className="not-loaded"
              srcSet={game.srcset}
              alt={name}
              height={common.imgHeight}
              width={common.imgWidth}
            />
          </div>
          <Badges ids={game.casino_badge_ids} badges={common.badges} />
          <span className={`name ${nameSize}`} id={gameLabelId}>
            {name}
          </span>
        </a>
      </div>
    </li>
  );
};

const Error = ({ accessBlock }) => <div className="rs-panel callout rs-radius">{accessBlock}</div>;

const Loading = () => (
  <div style={{ position: 'fixed', right: 0 }}>
    <CircularProgress mode="indeterminate" />
  </div>
);

const Games = ({ launch, common }) => (
  <InfiniteScroll>
    {(currentGames) => (
      <ul
        className={`games_list pt_games_list rs-small-block-grid-4 rs-large-block-grid-6 rs-medium-block-grid-5 ${
          currentGames.length ? 'pt_games_list__loaded' : ''
        }`}
      >
        {currentGames.map((g) => (
          <Game key={g.id} game={g} launch={launch} common={common} />
        ))}
      </ul>
    )}
  </InfiniteScroll>
);

const Gallery = ({ common, locales, block, client, showFilters, gameTypes }) => {
  const [filters, updateFilters] = useFilters();
  const [loading, accessBlock, launch] = useGallery(block, client);

  return (
    <>
      {loading ? <Loading /> : null}
      {accessBlock ? (
        <Error accessBlock={accessBlock} />
      ) : (
        <>
          {showFilters && (
            <Filters filters={filters} updateFilters={updateFilters} locales={locales} types={gameTypes} />
          )}
          <Games key={JSON.stringify(filters)} launch={launch} common={common} />
        </>
      )}
    </>
  );
};

const PlaytechGallery = (props) => <Gallery {...props} />;

export default PlaytechGallery;
